"use client";

import { useEffect, useState, useMemo, ReactNode, ChangeEvent } from "react";
import { PageLayout } from "@/components/ui/PageLayout";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ArrowUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import Image from "next/image";
import Avatar from "boring-avatars";

// ── Types ────────────────────────────────────────────────────────────────────

interface LendingReserve {
  asset: string;
  symbol: string;
  decimals: number;
  totalSupplied: number;
  totalBorrowed: number;
  utilization: number;
  lastUpdated: string;
}

interface LendingPool {
  poolAddress: string;
  protocol: string;
  poolName: string;
  reserves: LendingReserve[];
  reserveCount: number;
}

interface LendingResponse {
  pools: LendingPool[];
  poolCount: number;
}

/** Pool-level aggregated row for the table. One row = one pool. */
interface AggregatedPool {
  poolAddress: string;
  poolName: string;
  protocol: string;
  reserves: LendingReserve[];
  reserveCount: number;
  totalSupplied: number;
  totalBorrowed: number;
  utilization: number;
}

type SortKey = "poolName" | "totalSupplied" | "totalBorrowed" | "utilization" | "reserveCount";
interface SortConfig { key: SortKey | null; direction: "asc" | "desc" | null; }

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatAmount(value: number): string {
  if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(2)}B`;
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(2)}K`;
  return `$${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

// ── Token Icon (same 3-tier fallback as TokenTableParts.tsx:157-180) ─────────

const KNOWN_ICONS: Record<string, string> = {
  XLM: "/token-logos/xlm.png",
  USDC: "/icons/tokens/usdc.png",
  EURC: "/icons/tokens/eurc.jpg",
  AQUA: "/icons/tokens/aqua.jpg",
};

function TokenIcon({ asset, symbol, size = 20, logos }: { asset: string; symbol: string; size?: number; logos: Record<string, string> }) {
  const src = logos[asset] ?? KNOWN_ICONS[symbol.toUpperCase()] ?? null;
  if (src) {
    return <Image src={src} alt={symbol} width={size} height={size} className="rounded-full object-cover" />;
  }
  return <Avatar size={size} name={asset} variant="beam" colors={["#1a1a2e", "#16213e", "#0f3460", "#533483", "#e94560"]} />;
}

function PoolAssetIcons({ reserves, logos }: { reserves: LendingReserve[]; logos: Record<string, string> }) {
  const MAX = 4;
  const shown = reserves.slice(0, MAX);
  const overflow = reserves.length - MAX;
  return (
    <div className="flex items-center -space-x-1.5">
      {shown.map((r) => (
        <div key={r.asset} className="relative w-5 h-5 rounded-full overflow-hidden border border-[#222] bg-[#141414]">
          <TokenIcon asset={r.asset} symbol={r.symbol} size={20} logos={logos} />
        </div>
      ))}
      {overflow > 0 && (
        <div className="w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-medium"
          style={{ background: "#1e1e1e", color: "var(--text-secondary)", border: "1px solid #333" }}>
          +{overflow}
        </div>
      )}
      <span className="ml-2 text-[11px]" style={{ color: "var(--text-secondary)" }}>
        {reserves.map((r) => r.symbol).join(", ")}
      </span>
    </div>
  );
}

// ── Utilization Bar ──────────────────────────────────────────────────────────

function UtilizationBar({ value }: { value: number }) {
  const pct = Math.min(value * 100, 100);
  const color = value > 0.8 ? "#f87171" : value > 0.5 ? "#fbbf24" : "#4ade80";
  return (
    <div className="flex items-center gap-2 justify-end">
      <div className="w-16 h-[4px] rounded-full overflow-hidden flex-shrink-0" style={{ background: "#1e1e1e" }}
        role="progressbar" aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100}>
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="text-[12px] tabular-nums min-w-[42px] text-right" style={{ color }}>{pct.toFixed(1)}%</span>
    </div>
  );
}

// ── Top Widgets (pool-level, NOT reserve-level) ──────────────────────────────

function TopLendingPools({ pools }: { pools: AggregatedPool[] }) {
  const topByUtil = useMemo(() => [...pools].sort((a, b) => b.utilization - a.utilization).slice(0, 5), [pools]);
  const topBySupplied = useMemo(() => [...pools].sort((a, b) => b.totalSupplied - a.totalSupplied).slice(0, 5), [pools]);
  const topByBorrowed = useMemo(() => [...pools].sort((a, b) => b.totalBorrowed - a.totalBorrowed).slice(0, 5), [pools]);

  const rankColor = (i: number) => i === 0 ? "#c8ff5e" : i === 1 ? "#a0a0a0" : "#8a6a4a";

  return (
    <div className="topTokensOverview">
      <div className="transform-gpu transition-transform hover:scale-[1.01] anim-fadeSlideInUp-300ms" style={{ animationDelay: "0.05s" }}>
        <div className="stat-card-wrap">
          <div className="stat-card-head">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
            </svg>
            TOP BY UTILIZATION
          </div>
          {topByUtil.map((p, i) => {
            const color = p.utilization > 0.8 ? "#f87171" : p.utilization > 0.5 ? "#fbbf24" : "#4ade80";
            return (
              <div key={p.poolAddress} className="stat-row">
                <span className="stat-rank" style={{ color: rankColor(i) }}>#{i + 1}</span>
                <span className="stat-pair">{p.poolName}</span>
                <span style={{ color, fontSize: 11 }}>{(p.utilization * 100).toFixed(1)}%</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="transform-gpu transition-transform hover:scale-[1.01] anim-fadeSlideInUp-300ms" style={{ animationDelay: "0.1s" }}>
        <div className="stat-card-wrap">
          <div className="stat-card-head">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
            TOP BY SUPPLIED
          </div>
          {topBySupplied.map((p, i) => (
            <div key={p.poolAddress} className="stat-row">
              <span className="stat-rank" style={{ color: rankColor(i) }}>#{i + 1}</span>
              <span className="stat-pair">{p.poolName}</span>
              <span className="val-muted">{formatAmount(p.totalSupplied)}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="transform-gpu transition-transform hover:scale-[1.01] anim-fadeSlideInUp-300ms" style={{ animationDelay: "0.15s" }}>
        <div className="stat-card-wrap">
          <div className="stat-card-head">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><path d="M9 14l2 2 4-4" />
            </svg>
            TOP BY BORROWED
          </div>
          {topByBorrowed.map((p, i) => (
            <div key={p.poolAddress} className="stat-row">
              <span className="stat-rank" style={{ color: rankColor(i) }}>#{i + 1}</span>
              <span className="stat-pair">{p.poolName}</span>
              <span className="val-muted">{formatAmount(p.totalBorrowed)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Skeleton ─────────────────────────────────────────────────────────────────

function LendingSkeleton() {
  return (
    <div className="space-y-4">
      <div className="topTokensOverview">
        {[0, 1, 2].map((i) => (
          <div key={i} className="rounded-xl overflow-hidden animate-pulse"
            style={{ background: "var(--surface)", border: "1px solid #1e1e1e", height: 140, opacity: 0.7 - i * 0.15 }} />
        ))}
      </div>
      <div className="table-wrap animate-pulse" style={{ height: 300 }} />
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────

export default function LendingPage() {
  const [data, setData] = useState<LendingResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [logos, setLogos] = useState<Record<string, string>>({});

  // Table state — matches PoolsTable exactly
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: null, direction: null });

  // Fetch data
  useEffect(() => {
    const baseUri = process.env.NEXT_PUBLIC_BASE_DATA_URI ?? "https://api-dev.hoops.fi";
    fetch(`${baseUri}/lending`)
      .then((res) => { if (!res.ok) throw new Error(`HTTP ${res.status}`); return res.json() as Promise<LendingResponse>; })
      .then((d) => { setData(d); setLoading(false); })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : "Failed to load lending data");
        setLoading(false);
      });
  }, []);

  // Fetch token logos manifest (client-side)
  useEffect(() => {
    fetch("/token-logos.json")
      .then((res) => res.json() as Promise<{ logos?: Record<string, string> } & Record<string, string>>)
      .then((d) => { setLogos(d.logos ?? d as Record<string, string>); })
      .catch(() => { /* silent — fallback logos used */ });
  }, []);

  // Aggregate pools: one entry per pool with summed metrics
  const aggregatedPools = useMemo<AggregatedPool[]>(() => {
    if (!data) return [];
    return data.pools.map((pool) => {
      let totalSupplied = 0;
      let totalBorrowed = 0;
      let weightedUtilSum = 0;
      let weightSum = 0;
      for (const r of pool.reserves) {
        const supplied = r.totalSupplied / Math.pow(10, r.decimals);
        const borrowed = r.totalBorrowed / Math.pow(10, r.decimals);
        totalSupplied += supplied;
        totalBorrowed += borrowed;
        weightedUtilSum += r.utilization * supplied;
        weightSum += supplied;
      }
      return {
        poolAddress: pool.poolAddress,
        poolName: pool.poolName,
        protocol: pool.protocol,
        reserves: pool.reserves,
        reserveCount: pool.reserveCount,
        totalSupplied,
        totalBorrowed,
        utilization: weightSum > 0 ? weightedUtilSum / weightSum : 0,
      };
    });
  }, [data]);

  // Search filter
  const filteredPools = useMemo(() => {
    if (!searchQuery) return aggregatedPools;
    const q = searchQuery.toLowerCase();
    return aggregatedPools.filter((p) =>
      p.poolName.toLowerCase().includes(q) ||
      p.reserves.some((r) => r.symbol.toLowerCase().includes(q))
    );
  }, [aggregatedPools, searchQuery]);

  // Sort — 3-state toggle matching PoolsTable:66-78
  const handleSort = (key: SortKey) => {
    setSortConfig((current) => {
      if (current.key === key) {
        if (current.direction === "asc") return { key, direction: "desc" };
        if (current.direction === "desc") return { key: null, direction: null };
      }
      return { key, direction: "asc" };
    });
  };

  const sortedPools = useMemo(() => {
    const d = [...filteredPools];
    const { key, direction } = sortConfig;
    if (!key || !direction) return d;
    const sign = direction === "asc" ? 1 : -1;
    d.sort((a, b) => {
      switch (key) {
        case "poolName": return a.poolName.localeCompare(b.poolName) * sign;
        case "totalSupplied": return (a.totalSupplied - b.totalSupplied) * sign;
        case "totalBorrowed": return (a.totalBorrowed - b.totalBorrowed) * sign;
        case "utilization": return (a.utilization - b.utilization) * sign;
        case "reserveCount": return (a.reserveCount - b.reserveCount) * sign;
        default: return 0;
      }
    });
    return d;
  }, [filteredPools, sortConfig]);

  // Pagination — matches PoolsTable:102-104
  const totalPages = Math.ceil(sortedPools.length / entriesPerPage);
  const startIndex = (currentPage - 1) * entriesPerPage;
  const paginatedPools = sortedPools.slice(startIndex, startIndex + entriesPerPage);

  useEffect(() => { setCurrentPage(1); }, [searchQuery, entriesPerPage]);

  // SortableHeader — exact copy from PoolsTable:129-152
  const SortableHeader = ({ children, sortKey, align = "left" }: { children: ReactNode; sortKey: SortKey; align?: "left" | "right" }) => (
    <TableHead onClick={() => { handleSort(sortKey); }} className={cn("cursor-pointer select-none", align === "right" ? "text-right" : "text-left")}>
      <div className="flex items-center gap-1" style={{ justifyContent: align === "right" ? "flex-end" : "flex-start" }}>
        <span>{children}</span>
        <ArrowUpDown className={`h-3 w-3 ${sortConfig.key === sortKey ? "text-foreground" : "text-muted-foreground/50"}`} />
      </div>
    </TableHead>
  );

  return (
    <PageLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="anim-fadeSlideInLeft-500ms">
          <div className="flex items-baseline gap-3">
            <h1 className="text-[28px] font-normal font-serif leading-tight">Lending</h1>
            {data && (
              <span className="text-[11px] tabular-nums px-2 py-[3px] rounded-full"
                style={{ background: "#141414", color: "var(--text-secondary)", border: "1px solid #222" }}>
                {data.poolCount} pools
              </span>
            )}
          </div>
          <p className="text-[11px] tracking-[0.04em] mt-1" style={{ color: "var(--text-secondary)" }}>
            Blend V2 lending protocol — supply assets to earn yield, borrow against collateral
          </p>
        </div>

        {loading && <LendingSkeleton />}

        {error && (
          <div className="flex items-start gap-3 px-4 py-3 rounded-xl" style={{ background: "#1a0d0d", border: "1px solid #3a1515" }} role="alert">
            <p className="text-[12px] font-medium" style={{ color: "#e05252" }}>Failed to load lending data: {error}</p>
          </div>
        )}

        {!loading && !error && data?.pools.length === 0 && (
          <div className="flex items-center justify-center py-16 rounded-xl" style={{ background: "var(--surface)", border: "1px solid #1e1e1e" }}>
            <p className="text-[13px]" style={{ color: "var(--text-secondary)" }}>No lending pools available yet. RPC is still syncing.</p>
          </div>
        )}

        {!loading && !error && data && data.pools.length > 0 && (
          <div className="space-y-5">
            {/* Top widgets — pool-level rankings */}
            <TopLendingPools pools={aggregatedPools} />

            {/* Filter bar — matches PoolsTable:157-208 */}
            <div className="filter-bar">
              <div className="search-wrap">
                <svg className="search-icon-pos" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.3-4.3" />
                </svg>
                <input
                  className="search-input"
                  placeholder="Search pools or assets..."
                  value={searchQuery}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => { setSearchQuery(e.target.value); }}
                />
              </div>
              {searchQuery && (
                <button className="pill" onClick={() => { setSearchQuery(""); }}>RESET</button>
              )}
            </div>

            {/* Table — shadcn primitives matching PoolsTable exactly */}
            <div className="table-wrap">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Protocol</TableHead>
                    <SortableHeader sortKey="poolName">Pool</SortableHeader>
                    <TableHead>Assets</TableHead>
                    <SortableHeader sortKey="totalSupplied" align="right">Total Supplied</SortableHeader>
                    <SortableHeader sortKey="totalBorrowed" align="right">Total Borrowed</SortableHeader>
                    <SortableHeader sortKey="utilization" align="right">Utilization</SortableHeader>
                    <SortableHeader sortKey="reserveCount" align="right">Reserves</SortableHeader>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedPools.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="table-cell-base">No lending pools found</TableCell>
                    </TableRow>
                  ) : (
                    paginatedPools.map((pool) => (
                      <TableRow key={pool.poolAddress} className="group table-row-hover">
                        <TableCell><Badge variant="blend" className="capitalize">Blend</Badge></TableCell>
                        <TableCell>
                          <span className="text-[12px] font-medium" style={{ color: "var(--text-primary)" }}>{pool.poolName}</span>
                        </TableCell>
                        <TableCell><PoolAssetIcons reserves={pool.reserves} logos={logos} /></TableCell>
                        <TableCell className="text-right" style={{ color: "var(--text-primary)" }}>{formatAmount(pool.totalSupplied)}</TableCell>
                        <TableCell className="text-right" style={{ color: "var(--text-primary)" }}>{formatAmount(pool.totalBorrowed)}</TableCell>
                        <TableCell className="text-right"><UtilizationBar value={pool.utilization} /></TableCell>
                        <TableCell className="text-right" style={{ color: "var(--text-secondary)" }}>{pool.reserveCount}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>

              {/* Pagination — exact copy from PoolsTable:297-363 */}
              {sortedPools.length > entriesPerPage && (
                <div className="table-footer">
                  <div className="table-footer-left">
                    <div className="table-footer-entries">
                      <span>Show</span>
                      <Select value={entriesPerPage.toString()} onValueChange={(v) => { setEntriesPerPage(parseInt(v, 10)); setCurrentPage(1); }}>
                        <SelectTrigger className="h-auto w-auto px-[8px] py-[3px] text-[10px] rounded-[var(--r)] shadow-none"
                          style={{ background: "var(--surface-hi)", border: "1px solid var(--border-subtle)", color: "var(--text-secondary)" }}>
                          <SelectValue placeholder="10" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10">10</SelectItem>
                          <SelectItem value="25">25</SelectItem>
                          <SelectItem value="50">50</SelectItem>
                          <SelectItem value="100">100</SelectItem>
                        </SelectContent>
                      </Select>
                      <span>entries</span>
                    </div>
                    <span>Showing {startIndex + 1} to {Math.min(startIndex + entriesPerPage, sortedPools.length)} of {sortedPools.length} entries</span>
                  </div>
                  <div className="table-footer-right">
                    <button className="btn-sm" onClick={() => { setCurrentPage((p) => Math.max(1, p - 1)); }} disabled={currentPage === 1}>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
                      Previous
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter((page) => page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1)
                      .map((page, index, array) => (
                        <span key={page}>
                          {index > 0 && array[index - 1] !== page - 1 && <span style={{ padding: "0 4px", color: "var(--text-tertiary)" }}>...</span>}
                          <button className={`page-num${currentPage === page ? " page-num-active" : ""}`} onClick={() => { setCurrentPage(page); }}>{page}</button>
                        </span>
                      ))}
                    <button className="btn-sm" onClick={() => { setCurrentPage((p) => Math.min(totalPages, p + 1)); }} disabled={currentPage === totalPages}>
                      Next
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
}
